/**
 * App.js - Root component and client-side router.
 *
 * Sets up all page routes and mounts the global idle-timeout watcher.
 * The DemoTour overlay is rendered at this level so it persists across
 * page navigations triggered by the tour itself.
 */

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import Landing from './pages/Landing';
import Demo from './pages/Demo';
import Verify from './pages/Verify';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import Accuracy from './pages/Accuracy';
import Compare from './pages/Compare';
import Profile from './pages/Profile';
import Logout from './pages/Logout';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TeamBuilder from './pages/TeamBuilder';
import Help from './pages/Help';
import DemoTour from './components/DemoTour';
import { startTourFn } from './tourRef';
import { api } from './utils/api';

// After 30 minutes of no input, automatically log the user out
const IDLE_MS = 30 * 60 * 1000;

/**
 * Invisible component that tracks user activity and logs them out when idle.
 * Attaches passive listeners on common interaction events and resets a timer
 * on each one. No render output — returns null.
 */
function IdleWatcher() {
  const navigate   = useNavigate();
  const timerRef   = useRef(null);

  useEffect(() => {
    const reset = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Only redirect if still logged in
        if (localStorage.getItem('access_token')) {
          api.logout();
          navigate('/');
        }
      }, IDLE_MS);
    };

    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    EVENTS.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset(); // start the timer immediately on mount
    return () => {
      clearTimeout(timerRef.current);
      EVENTS.forEach(e => window.removeEventListener(e, reset));
    };
  }, [navigate]);

  return null;
}

/**
 * Top-level app component. Wraps everything in BrowserRouter and defines
 * the route structure. DemoTour state lives here so the tour can navigate
 * across pages without unmounting.
 */
function App() {
  const [tourActive, setTourActive] = useState(false);

  // Expose a way for the Navbar's "Demo" button to trigger the tour
  startTourFn.current = () => setTourActive(true);

  return (
    <Router>
      <IdleWatcher />
      <Routes>
        {/* --- Routes --- */}
        <Route path="/"                element={<Landing />} />
        <Route path="/demo"            element={<Demo />} />
        <Route path="/verify"          element={<Verify />} />
        <Route path="/dashboard"       element={<Dashboard />} />
        <Route path="/analytics"       element={<Analytics />} />
        <Route path="/accuracy"        element={<Accuracy />} />
        <Route path="/compare"         element={<Compare />} />
        <Route path="/profile"         element={<Profile />} />
        <Route path="/logout"          element={<Logout />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/team-builder"    element={<TeamBuilder />} />
        <Route path="/help"            element={<Help />} />
      </Routes>

      {/* Tour overlay mounts on top of whatever page is currently shown */}
      {tourActive && <DemoTour onClose={() => setTourActive(false)} />}
    </Router>
  );
}

export default App;
